import { Lang } from '../types';

type Translations = Record<string, string>;
type I18nMap = Record<Lang, Translations>;

export const translations: I18nMap = {
  en: {
    // Nav
    nav_missions: 'Missions',
    nav_browse: 'Browse Missions',
    nav_create: 'Post a Mission',
    nav_mercenaries: 'People',
    nav_find: 'Find Mercenaries',
    nav_profile: 'My Profile',
    nav_chats: 'Chats',
    nav_signin: 'Sign In',
    nav_signout: 'Ride Off',

    // Hero
    hero_title: 'Modern Mercenaries',
    hero_sub: 'Post missions. Hire people. Get things done.',
    hero_browse: 'Browse Missions',
    hero_post: 'Post a Mission',

    // Stats
    stat_open: 'Open Missions',
    stat_mercs: 'Mercenaries',
    stat_circulating: 'Circulating',
    stat_fee: 'Platform Cut',

    // Missions
    mission_reward: 'Reward',
    mission_hours: 'Est. Hours',
    mission_category: 'Category',
    mission_location: 'Location',
    mission_skills: 'Required Skills',
    mission_desc: 'Description',
    mission_apply: 'Apply for Mission',
    mission_applied: 'Application Sent',
    mission_hire: 'Hire This Mercenary',
    mission_start: 'Mark as Started',
    mission_done: 'Mark as Complete',
    mission_status_open: 'OPEN',
    mission_status_active: 'ACTIVE',
    mission_status_started: 'IN PROGRESS',
    mission_status_done: 'COMPLETE',
    mission_status_cancelled: 'CANCELLED',
    mission_posted_by: 'Posted by',
    mission_fee_note: 'Platform fee (7%) is added on top of the reward.',
    mission_payment_progress: 'Payment Progress',
    mission_pay_hire: 'On Hire',
    mission_pay_start: 'On Start',
    mission_pay_finish: 'On Finish',

    // Create
    create_title: 'Post a New Mission',
    create_submit: 'Post Mission & Pay',
    create_preview: 'Fee Preview',
    create_amount: 'Mission amount',
    create_platform_fee: 'Platform fee (7%)',
    create_total: 'Total charged today',

    // Profile
    profile_edit: 'Edit Profile',
    profile_missions: 'My Missions',
    profile_active: 'Active Missions',
    profile_save: 'Save Profile',
    profile_bio: 'Bio',
    profile_skills: 'Skills',
    profile_add_skill: '+ Add Skill',
    profile_avatar_hint: 'Click to change photo',
    profile_avatar_limit: 'Max 350 KB · JPEG, PNG, GIF, WebP',

    // Mercs
    mercs_title: 'Find Mercenaries',
    mercs_search: 'Search by name or skill…',
    mercs_country: 'Country…',
    mercs_city: 'City…',
    mercs_view: 'View Profile',
    mercs_message: 'Message',

    // Chat
    chat_title: 'Dispatches',
    chat_placeholder: 'Type a message…',
    chat_send: 'Send',
    chat_select: 'Select a conversation',
    chat_hired_banner: '🤠 Hired!',
    chat_paid: 'paid',
    chat_remaining: 'remaining',

    // Auth
    auth_signin: 'Sign In',
    auth_register: 'Register',
    auth_email: 'Email',
    auth_password: 'Password',
    auth_password_hint: 'Min 8 characters',
    auth_firstname: 'First Name',
    auth_lastname: 'Last Name',
    auth_country: 'Country',
    auth_city: 'City',
    auth_login_btn: 'Ride In',
    auth_register_btn: 'Join the Outfit',

    // Reports
    report_title: 'Report User',
    report_reason: 'Reason',
    report_details: 'Details',
    report_submit: 'Submit Report',
    report_btn: 'Report',

    // Toasts
    toast_profile_saved: 'Profile saved!',
    toast_mission_created: 'Mission posted! Payment processed.',
    toast_applied: 'Applied! Chat opened with mission poster.',
    toast_hired: 'Mercenary hired! 10% payment sent.',
    toast_started: 'Mission started! 40% payment sent.',
    toast_done: 'Mission complete! Final payment sent.',
    toast_reported: 'Report submitted.',
    toast_blocked: 'User has been suspended after 3 reports.',
    toast_welcome: 'Welcome back',
    toast_joined: 'Welcome to the frontier',
    toast_logout: 'Rode off into the sunset…',

    // Errors
    err_fill_fields: 'Please fill all required fields.',
    err_email_invalid: 'Invalid email address.',
    err_password_short: 'Password must be at least 8 characters.',
    err_email_taken: 'Email already registered.',
    err_invalid_creds: 'Invalid email or password.',
    err_blocked: 'This account has been suspended.',
    err_own_mission: 'Cannot apply to your own mission.',
    err_already_applied: 'You have already applied.',
    err_self_report: 'Cannot report yourself.',
    err_already_reported: 'You have already reported this user.',
    err_image_size: 'Image must be under 350 KB.',
    err_image_format: 'Invalid image format.',
    err_max_skills: 'Maximum 10 skills allowed.',
    err_min_reward: 'Minimum reward is €5.',

    // Empty states
    empty_bounties: 'No missions in the territory yet.',
    empty_mercs: 'No mercenaries found.',
    empty_chats: 'No messages yet.',
    empty_missions: 'You have not posted any missions yet.',
    empty_active: 'No active missions.',

    // Latest
    latestBounties: 'Latest Missions',
  },

  fr: {
    nav_missions: 'Missions',
    nav_browse: 'Voir les Missions',
    nav_create: 'Créer une Mission',
    nav_mercenaries: 'Personnes',
    nav_find: 'Trouver des Mercenaires',
    nav_profile: 'Mon Profil',
    nav_chats: 'Messages',
    nav_signin: 'Connexion',
    nav_signout: 'Partir',

    hero_title: 'Modern Mercenaries',
    hero_sub: 'Postez des missions. Engagez des mercenaires. Faites avancer les choses.',
    hero_browse: 'Voir les Missions',
    hero_post: 'Créer une Mission',

    stat_open: 'Missions Ouvertes',
    stat_mercs: 'Mercenaires',
    stat_circulating: 'En Circulation',
    stat_fee: 'Commission',

    mission_reward: 'Récompense',
    mission_hours: 'Heures Est.',
    mission_category: 'Catégorie',
    mission_location: 'Lieu',
    mission_skills: 'Compétences Requises',
    mission_desc: 'Description',
    mission_apply: 'Postuler',
    mission_applied: 'Candidature Envoyée',
    mission_hire: 'Engager ce Mercenaire',
    mission_start: 'Marquer Démarrée',
    mission_done: 'Marquer Terminée',
    mission_status_open: 'OUVERT',
    mission_status_active: 'ACTIF',
    mission_status_started: 'EN COURS',
    mission_status_done: 'TERMINÉ',
    mission_status_cancelled: 'ANNULÉ',
    mission_posted_by: 'Posté par',
    mission_fee_note: 'La commission (7%) est ajoutée à la récompense.',
    mission_payment_progress: 'Avancement du Paiement',
    mission_pay_hire: 'À l\'embauche',
    mission_pay_start: 'Au démarrage',
    mission_pay_finish: 'À la fin',

    create_title: 'Créer une Nouvelle Mission',
    create_submit: 'Poster & Payer',
    create_preview: 'Aperçu des frais',
    create_amount: 'Montant mission',
    create_platform_fee: 'Commission (7%)',
    create_total: 'Total débité aujourd\'hui',

    profile_edit: 'Modifier le Profil',
    profile_missions: 'Mes Missions',
    profile_active: 'Missions Actives',
    profile_save: 'Enregistrer',
    profile_bio: 'Bio',
    profile_skills: 'Compétences',
    profile_add_skill: '+ Ajouter',
    profile_avatar_hint: 'Cliquer pour changer',
    profile_avatar_limit: 'Max 350 Ko · JPEG, PNG, GIF, WebP',

    mercs_title: 'Trouver des Mercenaires',
    mercs_search: 'Chercher par nom ou compétence…',
    mercs_country: 'Pays…',
    mercs_city: 'Ville…',
    mercs_view: 'Voir le Profil',
    mercs_message: 'Message',

    chat_title: 'Dépêches',
    chat_placeholder: 'Écrire un message…',
    chat_send: 'Envoyer',
    chat_select: 'Sélectionner une conversation',
    chat_hired_banner: '🤠 Engagé !',
    chat_paid: 'payé',
    chat_remaining: 'restant',

    auth_signin: 'Connexion',
    auth_register: 'Inscription',
    auth_email: 'Email',
    auth_password: 'Mot de passe',
    auth_password_hint: 'Min 8 caractères',
    auth_firstname: 'Prénom',
    auth_lastname: 'Nom',
    auth_country: 'Pays',
    auth_city: 'Ville',
    auth_login_btn: 'Se Connecter',
    auth_register_btn: 'Rejoindre',

    report_title: 'Signaler un Utilisateur',
    report_reason: 'Raison',
    report_details: 'Détails',
    report_submit: 'Soumettre',
    report_btn: 'Signaler',

    toast_profile_saved: 'Profil enregistré !',
    toast_mission_created: 'Mission postée ! Paiement traité.',
    toast_applied: 'Candidature envoyée !',
    toast_hired: 'Mercenaire engagé ! 10% envoyé.',
    toast_started: 'Mission démarrée ! 40% envoyé.',
    toast_done: 'Mission terminée ! Paiement final envoyé.',
    toast_reported: 'Signalement soumis.',
    toast_blocked: 'Compte suspendu après 3 signalements.',
    toast_welcome: 'Bon retour',
    toast_joined: 'Bienvenue sur la frontière',
    toast_logout: 'À bientôt…',

    err_fill_fields: 'Veuillez remplir tous les champs.',
    err_email_invalid: 'Email invalide.',
    err_password_short: 'Mot de passe trop court (8 min).',
    err_email_taken: 'Email déjà utilisé.',
    err_invalid_creds: 'Email ou mot de passe incorrect.',
    err_blocked: 'Ce compte a été suspendu.',
    err_own_mission: 'Impossible de postuler à votre propre mission.',
    err_already_applied: 'Vous avez déjà postulé.',
    err_self_report: 'Impossible de vous signaler.',
    err_already_reported: 'Vous avez déjà signalé cet utilisateur.',
    err_image_size: 'Image trop grande (max 350 Ko).',
    err_image_format: 'Format d\'image invalide.',
    err_max_skills: '10 compétences maximum.',
    err_min_reward: 'Récompense minimum : 5€.',

    empty_bounties: 'Aucune mission dans le territoire.',
    empty_mercs: 'Aucun mercenaire trouvé.',
    empty_chats: 'Aucun message pour l\'instant.',
    empty_missions: 'Vous n\'avez pas encore posté de mission.',
    empty_active: 'Aucune mission active.',

    latestBounties: 'Dernières Missions',
  },

  es: {
    nav_missions: 'Misiones',
    nav_browse: 'Ver Misiones',
    nav_create: 'Crear Misión',
    nav_mercenaries: 'Personas',
    nav_find: 'Buscar Mercenarios',
    nav_profile: 'Mi Perfil',
    nav_chats: 'Mensajes',
    nav_signin: 'Iniciar Sesión',
    nav_signout: 'Salir',

    hero_title: 'Modern Mercenaries',
    hero_sub: 'Publica misiones. Contrata mercenarios. Haz que pase.',
    hero_browse: 'Ver Misiones',
    hero_post: 'Crear Misión',

    stat_open: 'Misiones Abiertas',
    stat_mercs: 'Mercenarios',
    stat_circulating: 'En Circulación',
    stat_fee: 'Comisión',

    mission_reward: 'Recompensa',
    mission_hours: 'Horas Est.',
    mission_category: 'Categoría',
    mission_location: 'Ubicación',
    mission_skills: 'Habilidades Requeridas',
    mission_desc: 'Descripción',
    mission_apply: 'Aplicar',
    mission_applied: 'Solicitud Enviada',
    mission_hire: 'Contratar Mercenario',
    mission_start: 'Marcar Iniciada',
    mission_done: 'Marcar Completada',
    mission_status_open: 'ABIERTA',
    mission_status_active: 'ACTIVA',
    mission_status_started: 'EN PROGRESO',
    mission_status_done: 'COMPLETADA',
    mission_status_cancelled: 'CANCELADA',
    mission_posted_by: 'Publicada por',
    mission_fee_note: 'La comisión (7%) se añade a la recompensa.',
    mission_payment_progress: 'Progreso de Pago',
    mission_pay_hire: 'Al Contratar',
    mission_pay_start: 'Al Iniciar',
    mission_pay_finish: 'Al Terminar',

    create_title: 'Publicar Nueva Misión',
    create_submit: 'Publicar & Pagar',
    create_preview: 'Vista previa de tarifas',
    create_amount: 'Monto misión',
    create_platform_fee: 'Comisión (7%)',
    create_total: 'Total cobrado hoy',

    profile_edit: 'Editar Perfil',
    profile_missions: 'Mis Misiones',
    profile_active: 'Misiones Activas',
    profile_save: 'Guardar Perfil',
    profile_bio: 'Bio',
    profile_skills: 'Habilidades',
    profile_add_skill: '+ Agregar',
    profile_avatar_hint: 'Clic para cambiar foto',
    profile_avatar_limit: 'Máx 350 KB · JPEG, PNG, GIF, WebP',

    mercs_title: 'Buscar Mercenarios',
    mercs_search: 'Buscar por nombre o habilidad…',
    mercs_country: 'País…',
    mercs_city: 'Ciudad…',
    mercs_view: 'Ver Perfil',
    mercs_message: 'Mensaje',

    chat_title: 'Despachos',
    chat_placeholder: 'Escribe un mensaje…',
    chat_send: 'Enviar',
    chat_select: 'Selecciona una conversación',
    chat_hired_banner: '🤠 ¡Contratado!',
    chat_paid: 'pagado',
    chat_remaining: 'restante',

    auth_signin: 'Iniciar Sesión',
    auth_register: 'Registrarse',
    auth_email: 'Email',
    auth_password: 'Contraseña',
    auth_password_hint: 'Mín 8 caracteres',
    auth_firstname: 'Nombre',
    auth_lastname: 'Apellido',
    auth_country: 'País',
    auth_city: 'Ciudad',
    auth_login_btn: 'Entrar',
    auth_register_btn: 'Unirse al Grupo',

    report_title: 'Reportar Usuario',
    report_reason: 'Motivo',
    report_details: 'Detalles',
    report_submit: 'Enviar Reporte',
    report_btn: 'Reportar',

    toast_profile_saved: '¡Perfil guardado!',
    toast_mission_created: '¡Misión publicada! Pago procesado.',
    toast_applied: '¡Solicitud enviada!',
    toast_hired: '¡Mercenario contratado! 10% enviado.',
    toast_started: '¡Misión iniciada! 40% enviado.',
    toast_done: '¡Misión completa! Pago final enviado.',
    toast_reported: 'Reporte enviado.',
    toast_blocked: 'Usuario suspendido tras 3 reportes.',
    toast_welcome: 'Bienvenido de vuelta',
    toast_joined: 'Bienvenido a la frontera',
    toast_logout: 'Hasta pronto…',

    err_fill_fields: 'Por favor completa todos los campos.',
    err_email_invalid: 'Email inválido.',
    err_password_short: 'La contraseña debe tener al menos 8 caracteres.',
    err_email_taken: 'Email ya registrado.',
    err_invalid_creds: 'Email o contraseña incorrectos.',
    err_blocked: 'Esta cuenta ha sido suspendida.',
    err_own_mission: 'No puedes aplicar a tu propia misión.',
    err_already_applied: 'Ya aplicaste a esta misión.',
    err_self_report: 'No puedes reportarte a ti mismo.',
    err_already_reported: 'Ya reportaste a este usuario.',
    err_image_size: 'La imagen debe pesar menos de 350 KB.',
    err_image_format: 'Formato de imagen inválido.',
    err_max_skills: 'Máximo 10 habilidades.',
    err_min_reward: 'La recompensa mínima es €5.',

    empty_bounties: 'Aún no hay misiones en el territorio.',
    empty_mercs: 'No se encontraron mercenarios.',
    empty_chats: 'Aún no hay mensajes.',
    empty_missions: 'No has publicado ninguna misión.',
    empty_active: 'No tienes misiones activas.',

    latestBounties: 'Últimas Misiones',
  },
};

export function t(lang: Lang, key: string): string {
  return translations[lang]?.[key] ?? translations['en']?.[key] ?? key;
}
